import type { components } from '@/lib/api/generated/schema'
import { api } from '@/lib/api'

type Schemas = components['schemas']

export type ActivityChatSummary = Schemas['ActivityChatSummary']
export type PrivateConversation = Schemas['PrivateConversation']
export type ActivityMessage = Schemas['ActivityMessage']
export type PrivateMessage = Schemas['PrivateMessage']
export type ActivityDetail = Schemas['ActivityDetail']
export type Participant = Schemas['Participant']
export type Friend = Schemas['Friend']
export type ActivityProjection = Schemas['ActivityProjection']

const segment = encodeURIComponent

interface RequestOptions {
  signal?: AbortSignal
}

/**
 * Frozen chat boundary (`API_CONTRACT.md` §5, §6.15, §6.16 and OpenAPI).
 *
 * Nothing is cached here (D-042): screens own the request lifecycle and
 * reconcile mutations with the server response. Every path segment is encoded
 * before it reaches the shared fetch client.
 */
export const chatApi = {
  listActivityChats(
    options: RequestOptions = {},
  ): Promise<Schemas['ActivityChatsResponse']> {
    return api.get<Schemas['ActivityChatsResponse']>('/activities/chats', {
      signal: options.signal,
    })
  },

  getActivity(
    activityId: string,
    options: RequestOptions = {},
  ): Promise<ActivityDetail> {
    return api.get<ActivityDetail>(`/activities/${segment(activityId)}`, {
      signal: options.signal,
    })
  },

  listParticipants(
    activityId: string,
    options: RequestOptions = {},
  ): Promise<Schemas['ParticipantList']> {
    return api.get<Schemas['ParticipantList']>(
      `/activities/${segment(activityId)}/participants`,
      { signal: options.signal },
    )
  },

  listActivityMessages(
    activityId: string,
    options: RequestOptions = {},
  ): Promise<Schemas['ActivityMessageList']> {
    return api.get<Schemas['ActivityMessageList']>(
      `/activities/${segment(activityId)}/messages`,
      { signal: options.signal },
    )
  },

  sendActivityMessage(
    activityId: string,
    body: string,
  ): Promise<Schemas['ActivityMessageList']> {
    return api.post<Schemas['ActivityMessageList']>(
      `/activities/${segment(activityId)}/messages`,
      { body },
    )
  },

  readActivityMessages(
    activityId: string,
    lastMessageId: string,
  ): Promise<{ ok: true }> {
    return api.post<{ ok: true }>(
      `/activities/${segment(activityId)}/messages/read`,
      { lastMessageId },
    )
  },

  pinActivityChat(activityId: string, pinned: boolean): Promise<{ pinned: boolean }> {
    return api.patch<{ pinned: boolean }>(
      `/activities/${segment(activityId)}/chat-pin`,
      { pinned },
    )
  },

  hideActivityChat(activityId: string): Promise<{ hidden: true }> {
    return api.delete<{ hidden: true }>(
      `/activities/${segment(activityId)}/chat`,
    )
  },

  deleteActivityMessage(
    activityId: string,
    messageId: string,
  ): Promise<{ ok: true }> {
    return api.delete<{ ok: true }>(
      `/activities/${segment(activityId)}/messages/${segment(messageId)}`,
    )
  },

  listPrivateConversations(
    options: RequestOptions = {},
  ): Promise<Schemas['PrivateConversationList']> {
    return api.get<Schemas['PrivateConversationList']>(
      '/private-conversations',
      { signal: options.signal },
    )
  },

  openPrivateConversation(userId: string): Promise<{ id: string }> {
    return api.post<{ id: string }>(
      `/private-conversations/${segment(userId)}`,
    )
  },

  pinPrivateConversation(
    conversationId: string,
    pinned: boolean,
  ): Promise<{ pinned: boolean }> {
    return api.patch<{ pinned: boolean }>(
      `/private-conversations/${segment(conversationId)}/pin`,
      { pinned },
    )
  },

  hidePrivateConversation(
    conversationId: string,
  ): Promise<{ hidden: true; cleared: true }> {
    return api.delete<{ hidden: true; cleared: true }>(
      `/private-conversations/${segment(conversationId)}`,
    )
  },

  listPrivateMessages(
    conversationId: string,
    options: RequestOptions = {},
  ): Promise<Schemas['PrivateMessageList']> {
    return api.get<Schemas['PrivateMessageList']>(
      `/private-conversations/${segment(conversationId)}/messages`,
      { signal: options.signal },
    )
  },

  sendPrivateMessage(
    conversationId: string,
    body: string,
  ): Promise<Schemas['PrivateMessageSent']> {
    return api.post<Schemas['PrivateMessageSent']>(
      `/private-conversations/${segment(conversationId)}/messages`,
      { body },
    )
  },

  readPrivateMessages(
    conversationId: string,
    lastMessageId: string,
  ): Promise<{ ok: true }> {
    return api.post<{ ok: true }>(
      `/private-conversations/${segment(conversationId)}/messages/read`,
      { lastMessageId },
    )
  },

  listFriends(options: RequestOptions = {}): Promise<Schemas['FriendsResponse']> {
    return api.get<Schemas['FriendsResponse']>('/friends', {
      signal: options.signal,
    })
  },

  searchActivities(
    query: string,
    options: RequestOptions = {},
  ): Promise<Schemas['ActivityListResponse']> {
    return api.get<Schemas['ActivityListResponse']>('/activities', {
      query: { q: query },
      signal: options.signal,
    })
  },
}
